/**
 * Export utilities for React Flow diagrams
 * NEW APPROACH: Calculate bounds from actual node positions, no scaling needed!
 *
 * Key approach:
 * 1. Use html-to-image@1.11.11 (EXACT version - later versions broken)
 * 2. Calculate actual bounds from extreme node positions
 * 3. Set dynamic image size based on bounds + padding
 * 4. Simple translate transform (no scaling!)
 * 5. Convert icons to data URIs before export (fixes icon loading)
 */

import type { Node, Edge } from '@xyflow/react'
import { toPng, toSvg } from 'html-to-image'
import { jsPDF } from 'jspdf'
import { insertPngTextChunk, insertSvgMetadata } from './embeddedDiagram'

/** Keyword used for the embedded diagram payload in PNG/SVG exports. */
export const EMBEDDED_DIAGRAM_KEYWORD = 'graphingv2-diagram'

export interface ExportOptions {
  backgroundColor?: 'white' | 'transparent'
  padding?: number
  quality?: number
  imageWidth?: number
  imageHeight?: number
}

/**
 * Decode a base64 string to a Uint8Array (browser-safe).
 */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Encode a Uint8Array to a base64 string (browser-safe).
 */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode.apply(null, chunk as unknown as number[])
  }
  return btoa(binary)
}

/**
 * Decode a base64 string to a UTF-8 string (browser-safe).
 */
function base64ToString(base64: string): string {
  const bytes = base64ToBytes(base64)
  return new TextDecoder('utf-8').decode(bytes)
}

/**
 * Trigger a browser download for the given href.
 */
function triggerDownload(href: string, fileName: string): void {
  const link = document.createElement('a')
  link.download = fileName
  link.href = href
  link.click()
}

/**
 * Calculate actual bounds from node positions
 * Uses measured/computed dimensions for accurate sizing
 */
function calculateActualBounds(nodes: Node[], padding: number = 20) {
  if (nodes.length === 0) {
    return {
      x: 0,
      y: 0,
      width: 800,
      height: 600
    }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  // Find extreme positions accounting for node size and nodeOrigin [0.5, 0.5]
  nodes.forEach(node => {
    // Use measured dimensions (React Flow 12) or fallback to width property
    const width = node.measured?.width || node.width || 150
    const height = node.measured?.height || node.height || 150

    // Since nodeOrigin is [0.5, 0.5], position is at center
    // So actual bounds are position ± half dimensions
    const nodeMinX = node.position.x - width / 2
    const nodeMinY = node.position.y - height / 2
    const nodeMaxX = node.position.x + width / 2
    const nodeMaxY = node.position.y + height / 2

    minX = Math.min(minX, nodeMinX)
    minY = Math.min(minY, nodeMinY)
    maxX = Math.max(maxX, nodeMaxX)
    maxY = Math.max(maxY, nodeMaxY)
  })

  console.log('Node bounds debug:', {
    minX, minY, maxX, maxY,
    width: maxX - minX,
    height: maxY - minY,
    sampleNode: nodes[0] ? {
      id: nodes[0].id,
      position: nodes[0].position,
      width: nodes[0].measured?.width || nodes[0].width,
      height: nodes[0].measured?.height || nodes[0].height
    } : null
  })

  // Add padding
  return {
    x: minX - padding,
    y: minY - padding,
    width: (maxX - minX) + padding * 2,
    height: (maxY - minY) + padding * 2
  }
}

/**
 * Convert image URL to data URI
 * This fixes the icon loading issue - icons must be embedded as data URIs
 */
async function imageToDataUri(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0)

      try {
        const dataUri = canvas.toDataURL('image/png')
        resolve(dataUri)
      } catch (e) {
        reject(e)
      }
    }

    img.onerror = () => {
      // If CORS fails, try without crossOrigin
      const img2 = new Image()
      img2.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img2.width
        canvas.height = img2.height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img2, 0, 0)
          resolve(canvas.toDataURL('image/png'))
        } else {
          reject(new Error('Failed to get canvas context'))
        }
      }
      img2.onerror = () => reject(new Error(`Failed to load image: ${url}`))
      img2.src = url
    }

    img.src = url
  })
}

/**
 * Convert all icon images in the viewport to data URIs
 * This is CRITICAL - html-to-image cannot capture external images due to CORS
 */
async function convertIconsToDataUris(viewportElement: HTMLElement): Promise<Map<HTMLImageElement, string>> {
  // Try multiple selectors to find images
  const imgElements = viewportElement.querySelectorAll('img')
  const svgImages = viewportElement.querySelectorAll('image') // SVG <image> elements
  const allImages = [...Array.from(imgElements), ...Array.from(svgImages)] as HTMLImageElement[]

  const dataUriMap = new Map<HTMLImageElement, string>()

  console.log(`Found ${imgElements.length} <img> tags and ${svgImages.length} <image> tags`)
  console.log('All images:', allImages.map(img => ({
    tag: img.tagName,
    src: img.src || img.getAttribute('href') || img.getAttribute('xlink:href'),
    classList: Array.from(img.classList || [])
  })))

  const promises = Array.from(allImages).map(async (img) => {
    const src = img.src || img.getAttribute('href') || img.getAttribute('xlink:href')

    // Skip if no src or already data URI
    if (!src || src.startsWith('data:')) {
      return
    }

    try {
      const dataUri = await imageToDataUri(src)
      dataUriMap.set(img, dataUri)
      console.log(`✓ Converted: ${src.substring(0, 60)}...`)
    } catch (e) {
      console.warn(`✗ Failed to convert image: ${src}`, e)
    }
  })

  await Promise.all(promises)
  console.log(`Successfully converted ${dataUriMap.size} images`)
  return dataUriMap
}

/**
 * Hide edge label backgrounds temporarily for clean export
 */
function hideEdgeLabelBackgrounds(viewportElement: HTMLElement): () => void {
  const originalStyles = new Map<Element, string>()

  // Target all edge label related elements
  const selectors = [
    '.react-flow__edge-text',
    '.react-flow__edge-textwrapper',
    '.react-flow__edge-textbg',
    'foreignObject rect', // SVG rectangles in foreignObject (edge labels)
    '.react-flow__edge text'
  ]

  selectors.forEach(selector => {
    const elements = viewportElement.querySelectorAll(selector)
    elements.forEach((el) => {
      const htmlEl = el as HTMLElement | SVGElement

      if (htmlEl instanceof HTMLElement) {
        originalStyles.set(el, htmlEl.style.cssText)
        htmlEl.style.backgroundColor = 'transparent'
        htmlEl.style.background = 'transparent'
      } else if (htmlEl instanceof SVGRectElement) {
        // For SVG rect elements (edge label backgrounds)
        originalStyles.set(el, htmlEl.getAttribute('fill') || '')
        htmlEl.setAttribute('fill', 'transparent')
        htmlEl.setAttribute('opacity', '0')
      }
    })
  })

  // Return restore function
  return () => {
    originalStyles.forEach((style, el) => {
      if (el instanceof HTMLElement) {
        el.style.cssText = style
      } else if (el instanceof SVGRectElement) {
        el.setAttribute('fill', style)
        el.removeAttribute('opacity')
      }
    })
  }
}

/**
 * Temporarily replace images with data URIs, export, then restore
 */
async function withDataUriImages<T>(
  viewportElement: HTMLElement,
  exportFn: () => Promise<T>
): Promise<T> {
  // Convert all images to data URIs
  const dataUriMap = await convertIconsToDataUris(viewportElement)

  // Store original sources
  const originalSources = new Map<HTMLImageElement, string>()

  // Replace with data URIs
  dataUriMap.forEach((dataUri, img) => {
    // Store original source (could be src or href for SVG)
    const originalSrc = img.src || img.getAttribute('href') || img.getAttribute('xlink:href') || ''
    originalSources.set(img, originalSrc)

    // Set data URI (handle both img and SVG image elements)
    if (img.tagName.toLowerCase() === 'img') {
      img.src = dataUri
    } else {
      // SVG <image> element
      img.setAttribute('href', dataUri)
      img.setAttribute('xlink:href', dataUri)
    }
  })

  // Hide edge label backgrounds
  const restoreEdgeLabels = hideEdgeLabelBackgrounds(viewportElement)

  // Wait a bit for DOM to update
  await new Promise(resolve => setTimeout(resolve, 100))

  try {
    // Perform export
    const result = await exportFn()
    return result
  } finally {
    // Restore original sources
    originalSources.forEach((src, img) => {
      if (img.tagName.toLowerCase() === 'img') {
        img.src = src
      } else {
        // SVG <image> element
        img.setAttribute('href', src)
        img.setAttribute('xlink:href', src)
      }
    })
    // Restore edge label backgrounds
    restoreEdgeLabels()
  }
}

/**
 * Export diagram to PNG - NEW APPROACH
 *
 * No scaling needed! Just:
 * 1. Calculate actual bounds from node positions
 * 2. Set image size to match bounds + padding
 * 3. Simple translate transform to capture exact region
 * 4. Works anywhere on canvas!
 */
export async function exportToPNG(
  nodes: Node[],
  fileName: string = 'diagram.png',
  options: ExportOptions = {},
  _edges?: Edge[],
  diagramJson?: string
): Promise<void> {
  const {
    backgroundColor = 'white',
    padding = 20,
    quality = 2
  } = options

  const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement
  if (!viewportElement) {
    throw new Error('React Flow viewport not found')
  }

  // Calculate actual bounds from extreme node positions
  const bounds = calculateActualBounds(nodes, padding)

  // Image size matches actual content size
  const imageWidth = bounds.width
  const imageHeight = bounds.height

  console.log('Export config (NEW APPROACH):', {
    bounds,
    imageWidth,
    imageHeight,
    padding,
    nodesCount: nodes.length
  })

  // Export with icon data URI conversion
  await withDataUriImages(viewportElement, async () => {
    const dataUrl = await toPng(viewportElement, {
      backgroundColor: backgroundColor === 'transparent' ? 'transparent' : '#ffffff',
      width: imageWidth,
      height: imageHeight,
      pixelRatio: quality,
      cacheBust: true,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        // Simple translate - no scaling!
        transform: `translate(${-bounds.x}px, ${-bounds.y}px)`
      },
      filter: (node) => {
        // Exclude UI controls from export
        if (
          node.classList &&
          (node.classList.contains('react-flow__minimap') ||
            node.classList.contains('react-flow__controls') ||
            node.classList.contains('react-flow__panel') ||
            node.classList.contains('react-flow__attribution'))
        ) {
          return false
        }
        return true
      }
    })

    // Embed the diagram JSON as a PNG tEXt chunk so the image is re-importable.
    let downloadHref = dataUrl
    if (diagramJson) {
      try {
        const base64 = dataUrl.split(',')[1] || ''
        const pngBytes = base64ToBytes(base64)
        const withChunk = insertPngTextChunk(
          pngBytes,
          EMBEDDED_DIAGRAM_KEYWORD,
          diagramJson
        )
        downloadHref = `data:image/png;base64,${bytesToBase64(withChunk)}`
      } catch (e) {
        // If embedding fails, fall back to the plain image rather than failing.
        console.warn('Failed to embed diagram JSON in PNG export:', e)
      }
    }

    // Download
    triggerDownload(downloadHref, fileName)
  })
}

/**
 * Render the diagram to a PNG data URL using the SAME bounds/translate path as
 * exportToPNG. Shared by PDF export and copy-as-image so they match the PNG
 * output exactly. Returns the data URL and the rendered image dimensions.
 */
async function renderDiagramToPngDataUrl(
  nodes: Node[],
  options: ExportOptions = {}
): Promise<{ dataUrl: string; width: number; height: number }> {
  const {
    backgroundColor = 'white',
    padding = 20,
    quality = 2
  } = options

  const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement
  if (!viewportElement) {
    throw new Error('React Flow viewport not found')
  }

  const bounds = calculateActualBounds(nodes, padding)
  const imageWidth = bounds.width
  const imageHeight = bounds.height

  return withDataUriImages(viewportElement, async () => {
    const dataUrl = await toPng(viewportElement, {
      backgroundColor: backgroundColor === 'transparent' ? 'transparent' : '#ffffff',
      width: imageWidth,
      height: imageHeight,
      pixelRatio: quality,
      cacheBust: true,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        transform: `translate(${-bounds.x}px, ${-bounds.y}px)`
      },
      filter: (node) => {
        if (
          node.classList &&
          (node.classList.contains('react-flow__minimap') ||
            node.classList.contains('react-flow__controls') ||
            node.classList.contains('react-flow__panel') ||
            node.classList.contains('react-flow__attribution'))
        ) {
          return false
        }
        return true
      }
    })
    return { dataUrl, width: imageWidth, height: imageHeight }
  })
}

/**
 * Export diagram to PDF.
 *
 * Renders the diagram to a PNG data URL (reusing the PNG render path), then
 * builds a jsPDF document sized to the image (orientation chosen by aspect
 * ratio) and saves it as `<title>.pdf`.
 */
export async function exportToPDF(
  nodes: Node[],
  fileName: string = 'diagram.pdf',
  options: ExportOptions = {},
  _edges?: Edge[]
): Promise<void> {
  const { dataUrl, width, height } = await renderDiagramToPngDataUrl(nodes, options)

  const orientation = width >= height ? 'landscape' : 'portrait'
  // Size the PDF page to the rendered image (in px units) so the diagram fills
  // the page 1:1 with no scaling/cropping.
  const pdf = new jsPDF({
    orientation,
    unit: 'px',
    format: [width, height],
  })

  pdf.addImage(dataUrl, 'PNG', 0, 0, width, height)
  pdf.save(fileName)
}

/**
 * Copy the diagram to the clipboard as a PNG image.
 *
 * Renders to a PNG Blob and writes it via the async Clipboard API. If image
 * clipboard write is unsupported (e.g. Firefox/Safari without flags), this
 * no-ops with a console hint rather than throwing.
 */
export async function copyDiagramAsImage(
  nodes: Node[],
  options: ExportOptions = {}
): Promise<void> {
  const canWriteImage =
    typeof ClipboardItem !== 'undefined' &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.write === 'function'

  if (!canWriteImage) {
    console.warn(
      '[export] Copy as image is not supported in this browser (ClipboardItem / clipboard.write unavailable).'
    )
    return
  }

  const { dataUrl } = await renderDiagramToPngDataUrl(nodes, options)
  const blob = await fetch(dataUrl).then((res) => res.blob())

  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ])
  } catch (e) {
    console.warn('[export] Failed to copy diagram image to clipboard:', e)
  }
}

/**
 * Export diagram to SVG - NEW APPROACH
 */
export async function exportToSVG(
  nodes: Node[],
  fileName: string = 'diagram.svg',
  options: ExportOptions = {},
  _edges?: Edge[],
  diagramJson?: string,
  scale: number = 1
): Promise<void> {
  const {
    backgroundColor = 'white',
    padding = 20
  } = options

  const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement
  if (!viewportElement) {
    throw new Error('React Flow viewport not found')
  }

  // Calculate actual bounds from extreme node positions
  const bounds = calculateActualBounds(nodes, padding)

  const imageWidth = bounds.width
  const imageHeight = bounds.height

  // Apply scale to the rendered SVG dimensions so SVG quality matches PNG's
  // pixelRatio-based quality control.
  const safeScale = scale && scale > 0 ? scale : 1
  const outputWidth = imageWidth * safeScale
  const outputHeight = imageHeight * safeScale

  // Export with icon data URI conversion
  await withDataUriImages(viewportElement, async () => {
    const dataUrl = await toSvg(viewportElement, {
      backgroundColor: backgroundColor === 'transparent' ? 'transparent' : '#ffffff',
      width: outputWidth,
      height: outputHeight,
      cacheBust: true,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        transform: `translate(${-bounds.x}px, ${-bounds.y}px) scale(${safeScale})`,
        transformOrigin: 'top left'
      },
      filter: (node) => {
        if (
          node.classList &&
          (node.classList.contains('react-flow__minimap') ||
            node.classList.contains('react-flow__controls') ||
            node.classList.contains('react-flow__panel') ||
            node.classList.contains('react-flow__attribution'))
        ) {
          return false
        }
        return true
      }
    })

    // Decode the SVG data URL back to a raw SVG string so we can embed the
    // diagram JSON metadata and download as a Blob.
    let svgString = dataUrl
    const commaIdx = dataUrl.indexOf(',')
    if (dataUrl.startsWith('data:image/svg+xml') && commaIdx !== -1) {
      const meta = dataUrl.slice(5, commaIdx) // e.g. "image/svg+xml;charset=utf-8;base64"
      const payload = dataUrl.slice(commaIdx + 1)
      svgString = meta.includes('base64')
        ? base64ToString(payload)
        : decodeURIComponent(payload)
    }

    if (diagramJson) {
      svgString = insertSvgMetadata(svgString, diagramJson)
    }

    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    triggerDownload(url, fileName)
    URL.revokeObjectURL(url)
  })
}

/**
 * Copy diagram to clipboard
 */
export async function copyToClipboard(_nodes: Node[]): Promise<void> {
  const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement
  if (!viewportElement) {
    throw new Error('React Flow viewport not found')
  }

  await withDataUriImages(viewportElement, async () => {
    const dataUrl = await toPng(viewportElement, {
      backgroundColor: '#ffffff',
      filter: (node) => {
        if (
          node.classList &&
          (node.classList.contains('react-flow__minimap') ||
            node.classList.contains('react-flow__controls') ||
            node.classList.contains('react-flow__panel'))
        ) {
          return false
        }
        return true
      }
    })

    const blob = await fetch(dataUrl).then(res => res.blob())
    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': blob
      })
    ])
  })
}
